import clsx from 'clsx';
import Image from 'next/image';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React, { FunctionComponent, useCallback, useMemo } from 'react';

import Typography from '@/components/typography';
import {
  LOCAL_STORAGE_SOCIAL_SIGN_UP_DATA,
  REDIRECT_FROM,
} from '@/constants/constants';
import { PLAT_MAP_URL } from '@/constants/urls';
import useWebStorage from '@/hooks/useWebStorage';
import { TLoginResponse } from '@/ory/accountManagement/login/submitLoginFlow';
import submitRegistrationFlow from '@/ory/accountManagement/register/submitRegisterFlow';
import { Message } from '@/ory/models/error';
import { LoginMethods } from '@/ory/models/submitMethods/methods';
import { useAuth } from '@/providers/auth-provider';
import { capitalizeFirstLetter } from '@/utils/common';
import Logger from '@/utils/logger';
import handleOryResponse from '@/utils/ory/handleOryResponse';
import { checkForRedirect } from '@/utils/validRedirect';

export type Step = 'first' | 'second' | 'final' | 'login';

export enum Providers {
  APPLE = 'apple',
  GOOGLE = 'google',
}

interface Props {
  location: 'login' | 'register';
  setError: React.Dispatch<React.SetStateAction<Message[]>>;
  setIsLoading: (loading: boolean) => void;
  setStep?: React.Dispatch<React.SetStateAction<Step>>;
  setOpenStepper?: React.Dispatch<React.SetStateAction<boolean>>;
  canSubmit: () => boolean;
  firstName?: string;
  lastName?: string;
  traits?: any;
  provider: Providers;
  overrideFlowId?: string;
}

const SSOButton: FunctionComponent<Props> = ({
  location,
  setError,
  setIsLoading,
  canSubmit,
  provider,
  firstName = undefined,
  lastName = undefined,
  setOpenStepper = undefined,
  setStep = undefined,
  traits = undefined,
  overrideFlowId = '',
}: Props) => {
  const router = useRouter();
  const query = useSearchParams();
  const { oryLogin } = useAuth();
  const pathname = usePathname();
  const queryParams = useSearchParams();

  const onPlatMap = useMemo(() => {
    return pathname?.includes(PLAT_MAP_URL);
  }, [pathname]);

  const { updateValue } = useWebStorage({ key: REDIRECT_FROM });

  const style = useMemo(() => {
    switch (provider) {
      case Providers.APPLE:
        return 'bg-gray-100 text-white';
      case Providers.GOOGLE:
        return 'bg-[#4285F4] text-white';
      default:
        return 'border-gray-100 bg-gray-100';
    }
  }, [provider]);

  const cacheSignUpDataOnRedirect = useCallback(
    (res: TLoginResponse, isSignUpFlow: boolean) => {
      const isSSORedirect =
        res.type === 'ory-error' &&
        (res?.error as any)?.id === 'browser_location_change_required';

      if (isSSORedirect) {
        try {
          const from = checkForRedirect(
            router,
            (query?.get('from') as string) ?? undefined
          );
          window.localStorage.setItem(
            LOCAL_STORAGE_SOCIAL_SIGN_UP_DATA,
            JSON.stringify({
              first_name: firstName,
              isSignUpFlow,
              last_name: lastName,
            })
          );

          if (from && typeof from === 'string') {
            window.localStorage.setItem(
              REDIRECT_FROM,
              JSON.stringify({ from })
            );
          }
        } catch {
          // eslint-disable-next-line no-empty
        }
      }
    },
    [firstName, lastName, router, query]
  );

  const onSubmit = async () => {
    try {
      if (!canSubmit()) return;

      setIsLoading?.(true);

      if (location === 'login') {
        if (onPlatMap)
          updateValue({
            from: `${pathname}${
              queryParams?.toString().length
                ? `?${queryParams?.toString()}`
                : ''
            }`,
          });
        oryLogin(
          {
            method: LoginMethods.OIDC,
            provider,
          },
          overrideFlowId
        ).then((res) => {
          cacheSignUpDataOnRedirect(res, false);
          handleOryResponse(res, {
            onSuccess: () => {},
            setError,
            setIsLoading,
          });
        });
      } else if (location === 'register') {
        await submitRegistrationFlow({
          method: LoginMethods.OIDC,
          provider,
          traits,
        }).then((res) => {
          cacheSignUpDataOnRedirect(res, true);
          handleOryResponse(res, {
            onSuccess: () => {},
            setError,
            setIsLoading,
          });
        });
      }

      if (onPlatMap && setOpenStepper && setStep) {
        setOpenStepper(false);
        setStep('first');
      }
    } catch (error) {
      Logger.error(error.message);
    }
  };

  return (
    <button
      className={clsx(
        'flex w-full items-center justify-center gap-x-3 px-4 py-2 shadow',
        style
      )}
      type="button"
      onClick={onSubmit}
    >
      <span className="h-[24px] w-[24px] text-current">
        <Image
          alt={provider}
          height={24}
          src={`/img/social-media/${provider}.svg`}
          width={24}
        />
      </span>
      <Typography
        className="text-center font-medium text-current"
        variant="body1"
      >
        Continue with {capitalizeFirstLetter(provider)}
      </Typography>
    </button>
  );
};

export default SSOButton;
